import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';

import { syncReadOnlyMenu, syncReadOnlyMenuViaOrchestrator } from 'helpers/menuManagement';
import { useCubohActionBar } from 'hooks/useCubohActionBar';
import MenuActions from 'components/MenuManagement/MenuActions/MenuActions';
import {
  useHandleSuccessNotification,
  useHandleErrorNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';
import { POS_SYNC_COPY } from 'constants/posSyncCopy';

// Keep the sync/publish result visible longer than the app's 2s default -
// it's a meaningful outcome the user is likely to be watching for, not a
// routine edit confirmation. Still manually closable at any time.
const POS_SYNC_NOTIFICATION_AUTO_CLOSE_MS = 15000;

export default function RefreshPOSModal({
  opened,
  close,
  merchantId,
  scenario = 'readOnlySync',
  menuCount,
}) {
  const { posOption, createListOfAvailableIntegrations } = useContext(MenuActions.Context);
  const { singleSelectedCompanyID } = useCubohActionBar();
  const handleSuccessNotification = useHandleSuccessNotification();
  const handleErrorNotification = useHandleErrorNotification();
  const copy = POS_SYNC_COPY[scenario];

  const handleConfirm = async () => {
    close();
    try {
      const integrationsList = await createListOfAvailableIntegrations(merchantId);
      if (
        !integrationsList.find(
          ({ integrationName }) => integrationName === posOption.integrationName,
        )
      ) {
        await syncReadOnlyMenuViaOrchestrator({ companyId: singleSelectedCompanyID });
      } else {
        await syncReadOnlyMenu({
          merchantId,
          companyId: singleSelectedCompanyID,
          source: {
            app: posOption.integrationName,
            id: null,
          },
        });
      }

      // Fires as soon as MM acknowledges the request - the actual sync/publish
      // work still happens async server-side, this only confirms dispatch.
      handleSuccessNotification({
        customMessage: copy.getSuccessMessage(
          posOption.readableAppName,
          dayjs().format('MMMM D @ h:mm a'),
        ),
        autoClose: POS_SYNC_NOTIFICATION_AUTO_CLOSE_MS,
        withCloseButton: true,
      });
    } catch (e) {
      handleErrorNotification({
        customMessage: `Error importing menus from ${posOption.readableAppName}, please try again`,
        autoClose: POS_SYNC_NOTIFICATION_AUTO_CLOSE_MS,
        withCloseButton: true,
      });
    }
  };

  return (
    <Modal opened={opened} onClose={close} title={copy.getModalTitle(menuCount)}>
      <Stack>
        {copy.getBody(posOption.readableAppName, menuCount).map(({ text, strong }, index) =>
          strong ? (
            <Text key={index} component="strong" variant="body-sm-bold">
              {text}
            </Text>
          ) : (
            <Text key={index}>{text}</Text>
          ),
        )}
        <Stack gap="xs">
          <Button fullWidth onClick={handleConfirm}>
            {copy.confirmLabel}
          </Button>
          <Button fullWidth variant="outline" onClick={close}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

RefreshPOSModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  merchantId: PropTypes.string.isRequired,
  scenario: PropTypes.oneOf(['readOnlySync', 'syncInPlace', 'syncAndPublish']),
  menuCount: PropTypes.number,
};
